/**
 * Site navigation: the menu toggle and the sticky state.
 *
 * Replaces assets/scripts/main.js, which was 142 lines of which about 120 were
 * dead — a bundled copy of the `classie` class helper for a browser without
 * classList, and Modernizr feature detection for transition events. Neither had
 * any other consumer on the site.
 *
 * Open state is a class on <body>, not on the nav: the scroll lock needs it
 * there anyway, and one flag beats two that can disagree. `aria-expanded` on
 * the button is kept in step with it.
 */

const OPEN = 'nav-open'
const STUCK = 'nav-stuck'

function initToggle (root, signal) {
  const toggle = root.querySelector('.nav-toggle')
  const nav = root.querySelector('.site-nav')
  if (!toggle || !nav) return

  /* Everything that must stay reachable while the overlay is up lives inside
     one subtree: the overlay itself, and the bar carrying the brand and the
     toggle. This finds the nearest ancestor holding all of them rather than
     naming a class, so the rule survives the markup being rearranged -- and
     if it ever cannot find one it stops at <body> and nothing goes inert,
     which is the behaviour this had before. */
  const brand = root.querySelector('.site-brand')
  const keep = (() => {
    const needed = [nav, toggle, brand].filter(Boolean)
    for (let node = nav; node && node !== document.body; node = node.parentElement) {
      if (needed.every((el) => node.contains(el))) return node
    }
    return document.body
  })()

  /* The siblings along the chain from that subtree up to <body>: the hero, the
     video, the arrow, <main>, the footer, the call button and the skip link.
     Collected on each open rather than once. The reason used to be the tab
     strip on /philosophie/, which replaced the hero element from a <template>;
     E46 removed that, and the habit stays because the stronger reason was
     always there -- <header> is a swup container, so this whole subtree is
     replaced on every visit and a list cached across one would hold detached
     nodes. */
  const outside = () => {
    const found = []
    for (let node = keep; node && node.parentElement; node = node.parentElement) {
      for (const sibling of node.parentElement.children) {
        if (sibling !== node && sibling.tagName !== 'SCRIPT') found.push(sibling)
      }
      if (node.parentElement === document.body) break
    }
    return found
  }

  /* `inert` is the half the Tab trap below cannot do. The trap keeps the TAB
     KEY inside the overlay, which is all a keyboard user needs; a VoiceOver
     user swipes rather than tabs, and swiping walks the accessibility tree
     straight past the trap into the page behind -- invisible content, read out
     as though the menu were not there. Removing that subtree from the tree is
     the only thing that stops it. a11y audit finding 2.2.

     Added alongside the trap, not in place of it: `inert` needs Safari 15.5
     and the bundle targets 14.5, so below that the trap is still the whole
     protection and the behaviour is exactly what it was. Ben's call. */
  const supportsInert = 'inert' in HTMLElement.prototype

  const setOpen = (open) => {
    document.body.classList.toggle(OPEN, open)
    toggle.setAttribute('aria-expanded', String(open))
    if (!supportsInert) return
    /* Read the list while it is still reachable; on close it does not matter
       which order the flags come off in. */
    for (const el of outside()) el.inert = open
  }

  toggle.addEventListener('click', () => {
    setOpen(!document.body.classList.contains(OPEN))
  }, { signal })

  /* Everything that can hold focus while the overlay is up, in tab order.
     The brand is in here because it stays visible over the overlay — anything
     the trap leaves out is on screen but unreachable by keyboard. Document
     order is brand, toggle, links, so the last link wraps back to the brand. */
  const stops = () => [brand, toggle, ...nav.querySelectorAll('a[href]')].filter(Boolean)

  document.addEventListener('keydown', (event) => {
    if (!document.body.classList.contains(OPEN)) return

    /* Escape closes it and puts focus back on the button. Without the second
       half, focus is left on a link inside an overlay that is now invisible. */
    if (event.key === 'Escape') {
      setOpen(false)
      toggle.focus()
      return
    }

    /* Focus trap. The overlay covers the page but does not remove it from the
       tab order, so without this the next Tab past the last link walks into
       content the user cannot see — the page behind, still fully tabbable.
       Kept as the floor under `inert` above, which says the same thing to the
       accessibility tree but needs Safari 15.5 against this bundle's 14.5. */
    if (event.key !== 'Tab') return

    const items = stops()
    const first = items[0]
    const last = items[items.length - 1]
    const active = document.activeElement
    const outside = !items.includes(active)

    if (event.shiftKey && (active === first || outside)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && (active === last || outside)) {
      event.preventDefault()
      first.focus()
    }
  }, { signal })

  /* Following a link closes it. The overlay is fixed, so on a same-page anchor
     it would otherwise stay up over the target. */
  nav.addEventListener('click', (event) => {
    const link = event.target.closest('a')
    if (!link) return
    setOpen(false)
    markFollowed(link, event)
  }, { signal })

  /* Handed back so the teardown can close the menu on the way out. This was
     the only thing holding it up until 2026-09-20: `inert` is set on the
     siblings along the chain up to <body>, and the footer, the skip link and
     the call button were none of them reconciled by a visit, so navigating
     with the menu open left them inert on the next page -- present, visible,
     and missing from the accessibility tree.

     All three are reconciled now: the footer and the skip link are Swup
     containers, and the call button is replaced, inserted or removed by
     swup.js's `content:replace`. A replaced element cannot carry a stale flag.
     Kept as the floor under that rather than as a duplicate of it -- it is one
     call, it runs on `before('content:replace')` while the markup it has to
     reach is still in the document, and it is the whole protection wherever
     `inert` is unsupported. */
  return () => setOpen(false)
}

/**
 * The old version set `window.onscroll` and read `navbar.offsetTop` on every
 * scroll event — a forced layout per frame, for a boolean that changes twice.
 *
 * A zero-height sentinel sits where the nav starts out. When it scrolls out of
 * view the page has scrolled past it, and the flag says so. `position: sticky`
 * cannot stand in: the nav is inside <header>, and a sticky element stops at
 * its own container's edge, so it would come unstuck as soon as the hero
 * scrolls away.
 *
 * On a phone the bar is fixed from the start since 2026-10-02, so the flag
 * only fades the bar's surface in and out, and the stylesheet places the
 * sentinel 48px down the page there (.nav-sentinel, components/_site-nav.scss).
 */
function initSticky (root, signal) {
  const nav = root.querySelector('.site-nav')
  if (!nav || !nav.parentNode) return

  const sentinel = document.createElement('div')
  sentinel.className = 'nav-sentinel'
  sentinel.setAttribute('aria-hidden', 'true')
  nav.parentNode.insertBefore(sentinel, nav)

  /* The flag goes on <body>, not on the nav, because the two breakpoints pin
     different elements: the menu row on desktop, the whole logo-and-toggle bar
     on mobile. Which one that is belongs in the stylesheet — this only reports
     that the header has scrolled past. */
  const observer = new IntersectionObserver(
    ([entry]) => document.body.classList.toggle(STUCK, !entry.isIntersecting),
    { threshold: 0 }
  )
  observer.observe(sentinel)

  signal.addEventListener('abort', () => {
    observer.disconnect()
    sentinel.remove()
  }, { once: true })
}

/* THE LINE UNDER A CLICKED LINK STAYS UNTIL THE PAGE HAS CHANGED, Ben
   2026-09-20, and it is the one thing in E47 § 3.1 that CSS cannot do on its
   own: click a nav link and move the pointer away, and `:hover` ends while
   the old page is still on screen -- so the half-strength line drew itself
   back into its centre, the visit finished, and the current page's line
   appeared. Shrink, pause, pop, for a reader who has already committed.

   A class says what the pointer no longer can. The stylesheet pins the line
   open on it (base/_links.scss, components/_section-nav.scss) and the marked element is destroyed by the
   swap a few hundred milliseconds later, so nothing has to take it off again
   on a visit that succeeds -- the timeout below is only for one that does
   not.

   MODIFIED CLICKS AND NEW TABS ARE NOT COMMITMENTS. A cmd-click leaves this
   page exactly where it is, and pinning a line open on a page the reader
   stays on would be a bug rather than a nicety. */
const FOLLOWED = 'is-following'
const FOLLOWED_TIMEOUT = 2000

function markFollowed (link, event) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  if (link.target === '_blank' || link.hasAttribute('download')) return
  if (link.origin && link.origin !== window.location.origin) return

  link.classList.add(FOLLOWED)
  /* The floor under a visit that never happens -- blocked, aborted, or a link
     Swup hands to the browser and the browser then declines. Long enough that
     it cannot fire inside a normal navigation, short enough to be over before
     the reader wonders about it. */
  setTimeout(() => link.classList.remove(FOLLOWED), FOLLOWED_TIMEOUT)
}

export function initNav (root = document) {
  /* The keydown listener is on the document, and the sentinel is inserted by
     this module rather than shipped in the markup -- so without a teardown a
     navigation leaves both behind and the next one adds a second of each. */
  const controller = new AbortController()
  const closeMenu = initToggle(root, controller.signal)
  initSticky(root, controller.signal)

  /* The section strip's links commit the same way the menu's do. */
  root.querySelector('.section-nav')?.addEventListener('click', (event) => {
    const link = event.target.closest('a')
    if (link) markFollowed(link, event)
  }, { signal: controller.signal })

  return () => {
    closeMenu?.()
    controller.abort()
  }
}
